<template>
  <div
    v-if="editMode === 'inline' && isEditingGeofenceGroup"
    id="edit_geofence_group"
    data-testid="edit_geofence_group">
    <edit-geofence-group-form
      :geofence-group-type="GeofenceGroupType.AUDIENCE"
      :geofence-group-i-d="isEditingNewGeofenceGroup ? null : geofenceGroup.id"
      :in-modal="true"
      :show-footer="true"
      :submit-emits="true"
      :alt-style="true"
      @complete="updateGeofenceGroup" />
  </div>
  <div
    v-else
    class="form-group row"
    data-testid="geofenceRule">
    <form-label
      for-i-d="edit_audiencerule_geofence"
      data-testid="edit_audiencerule_geofence_label"
      :is-required="true">
      {{ capitalize(i18n.t('names.geofenceGroup', 1)) }}
    </form-label>
    <div
      id="edit_audiencerule_geofence"
      class="col-sm-8"
      data-testid="edit_audiencerule_geofence">
      <div class="target-geofence">
        <multiselect
          v-model="geofenceGroup"
          track-by="id"
          label="name"
          :options="geofenceGroups"
          :searchable="true"
          :show-labels="false"
          :class="{ 'is-danger': validator.messageBag.errors.has('geofence') }"
          :allow-empty="false"
          @select="() => (isEditingGeofenceGroup = false)" />
        <button
          v-if="geofenceGroup"
          id="edit_geofence"
          v-tooltip="!isEditingGeofenceGroup && capitalize($t('actions.editGeofenceGroup'))"
          class="btn btn-primary-hollow btn-sm btn-no-text"
          data-testid="editGeofenceGroup"
          @click="editGeofenceGroup">
          <icon-edit />
        </button>
        <button
          id="add_geofence"
          v-tooltip="!isEditingGeofenceGroup && capitalize($t('actions.newGeofenceGroup'))"
          class="btn btn-primary-hollow btn-sm btn-no-text"
          data-testid="addGeofenceGroup"
          @click="addGeofenceGroup">
          <icon-add />
        </button>
      </div>
      <span
        v-show="validator.messageBag.errors.has('geofence')"
        class="help is-danger">
        {{ validator.messageBag.errors.first('geofence') }}
      </span>
    </div>
  </div>
  <transition-group name="squish fade">
    <template v-if="!isEditingGeofenceGroup || editMode !== 'inline'">
      <edit-daypart :audience-rule="audienceRule" />
      <edit-lookback-window
        :show-candidate-frequency="true"
        :audience-lookback-window="audienceRule.audienceLookbackWindow"
        :validator="validator" />
    </template>
  </transition-group>
  <modal
    v-if="editMode === 'modal' && isEditingGeofenceGroup"
    id="edit_geofence_group"
    class="edit-geofence-group"
    data-testid="edit_geofence_group"
    :use-full-width="true"
    :use-full-height="true"
    :max-height="true"
    :no-padding="true"
    :visible="isEditingGeofenceGroup"
    :title="
      capitalize(
        i18n.t(isEditingNewGeofenceGroup ? 'actions.newAudienceGeofenceGroup' : 'actions.editAudienceGeofenceGroup'),
      )
    "
    :button1-visible="false">
    <template #modal-after-form>
      <div class="flex-v-full">
        <edit-geofence-group-form
          :geofence-group-type="GeofenceGroupType.AUDIENCE"
          :geofence-group-i-d="isEditingNewGeofenceGroup ? null : geofenceGroup?.id"
          :in-modal="true"
          :show-footer="true"
          :submit-emits="true"
          :overwrite-geofence="true"
          @complete="updateGeofenceGroup" />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
  import { capitalize, computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Multiselect from 'vue-multiselect';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import { FormLabel } from '@/common/components/controls';
  import Modal from '@/common/components/controls/Modal.vue';
  import { IconAdd, IconEdit } from '@/common/icons/monochrome';
  import { GeofenceGroup } from '@/common/models';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import Validator from '@/common/models/audience/Validator';
  import GeofenceGroupType from '@/common/models/geofenceGroups/GeofenceGroupType.enum';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import EditDaypart from '@/org-app/components/audienceRules/edit/EditDaypart.vue';
  import EditLookbackWindow from '@/org-app/components/audienceRules/edit/EditLookbackWindow.vue';
  import EditGeofenceGroupForm from '@/org-app/components/geofenceGroups/EditGeofenceGroup.vue';
  import { mapActions } from '@/utils/StateMapping';

  // Setup
  const i18n = useI18n();
  const { updateAudienceRuleGeofenceGroupID, updateAudienceRuleName } = mapActions();
  const props = defineProps<{
    validator: Validator;
    geofenceGroups: GeofenceGroup[];
    audienceRule: AudienceRule;
    editMode: 'inline' | 'modal';
  }>();
  const emits = defineEmits<{
    (e: 'editGeofenceGroup'): void;
    (e: 'saveGeofenceGroup'): void;
  }>();

  // Ref
  const isEditingGeofenceGroup = ref(false);
  const isEditingNewGeofenceGroup = ref(false);

  // Computed
  const geofenceGroup = computed({
    get() {
      if (props.audienceRule === null) return null;
      return props.geofenceGroups.find((x) => x.id === props.audienceRule.geofenceGroupID);
    },
    set(value) {
      if (props.audienceRule === null || props.audienceRule === undefined) {
        return;
      }
      updateAudienceRuleGeofenceGroupID({
        audienceRule: props.audienceRule,
        value: value.id,
      });
      updateAudienceRuleName({
        audienceRule: props.audienceRule,
        value: value.name,
      });
    },
  });

  // Methods
  function updateGeofenceGroup(newGeofenceGroup?: GeofenceGroup) {
    isEditingGeofenceGroup.value = false;
    if (newGeofenceGroup) {
      geofenceGroup.value = newGeofenceGroup;
      GlobalEventBus.$emit(GlobalEvents.audienceLogicGeofenceGroupAdded, newGeofenceGroup.id);
    }
    emits('saveGeofenceGroup');
  }
  function addGeofenceGroup() {
    trackQuickCustomAudience();
    isEditingNewGeofenceGroup.value = true;
    isEditingGeofenceGroup.value = true;
    emits('editGeofenceGroup');
    GlobalEventBus.$emit(GlobalEvents.clearTooltips);
  }
  function editGeofenceGroup() {
    trackQuickCustomAudience(props.audienceRule.geofenceGroupID);
    isEditingNewGeofenceGroup.value = false;
    isEditingGeofenceGroup.value = true;
    emits('editGeofenceGroup');
    GlobalEventBus.$emit(GlobalEvents.clearTooltips);
  }
  function trackQuickCustomAudience(id = null) {
    const value = id ?? 'new';

    GoogleAnalyticsApi.sendEvent('quick_audience_geofence', { category: 'EDIT', value });
  }
</script>

<style lang="scss" scoped>
  .target-geofence {
    display: flex;
    gap: 1rem;
  }
</style>
